// src/pages/DetectionGallery.js
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  FaSearch,
  FaCalendar,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaUsers,
  FaTruck,
} from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

function DetectionOverlayImage({
  src,
  alt,
  boxes = [],
  className = "",
  imgClassName = "",
}) {
  const imgRef = useRef(null);
  const canvasRef = useRef(null);

  const drawBoxes = () => {
    const img = imgRef.current;
    const canvas = canvasRef.current;
    if (!img || !canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const displayWidth = img.clientWidth;
    const displayHeight = img.clientHeight;

    canvas.width = displayWidth;
    canvas.height = displayHeight;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!boxes || !boxes.length) return;
    if (!img.naturalWidth || !img.naturalHeight) return;

    const scaleX = displayWidth / img.naturalWidth;
    const scaleY = displayHeight / img.naturalHeight;

    ctx.lineWidth = 2;
    ctx.font = "14px Arial";

    boxes.forEach((box) => {
      const x = box.x1 * scaleX;
      const y = box.y1 * scaleY;
      const w = (box.x2 - box.x1) * scaleX;
      const h = (box.y2 - box.y1) * scaleY;
      const label = box.label || "object";

      const color = label === "person" ? "#3b82f6" : "#10b981";

      ctx.strokeStyle = color;
      ctx.fillStyle = color;
      ctx.strokeRect(x, y, w, h);

      const textWidth = ctx.measureText(label).width + 12;
      const textHeight = 22;
      const textX = x;
      const textY = Math.max(0, y - textHeight);

      ctx.fillRect(textX, textY, textWidth, textHeight);
      ctx.fillStyle = "#ffffff";
      ctx.fillText(label, textX + 6, textY + 15);
    });
  };

  useEffect(() => {
    drawBoxes();
    window.addEventListener("resize", drawBoxes);
    return () => window.removeEventListener("resize", drawBoxes);
  }, [boxes]);

  return (
    <div className={`relative ${className}`}>
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        onLoad={drawBoxes}
        className={imgClassName}
      />
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
      />
    </div>
  );
}

function DetectionGallery() {
  const [dateOptions, setDateOptions] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [images, setImages] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage] = useState(12);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);

  useEffect(() => {
    async function fetchDates() {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/detections/stats`);
        const sortedDates = [...res.data]
          .map((r) => r.date)
          .sort((a, b) => new Date(b) - new Date(a));
        setDateOptions(sortedDates);
      } catch (err) {
        console.error("Failed to load detection dates", err);
      }
    }
    fetchDates();
  }, []);

  useEffect(() => {
    setLoading(true);
    let params = `?page=${page}&per_page=${perPage}`;
    if (selectedDate) params += `&date=${selectedDate}`;

    axios
      .get(`${API_BASE_URL}/api/detections/gallery${params}`)
      .then((res) => {
        setImages(res.data.results || []);
        setTotal(res.data.total || 0);
      })
      .catch((err) => {
        console.error("Failed to load detection gallery", err);
        setImages([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [selectedDate, page, perPage]);

  useEffect(() => {
    if (!modalOpen) return;

    const handleKey = (e) => {
      if (e.key === "ArrowRight") {
        setModalIndex((i) => (i + 1) % images.length);
      } else if (e.key === "ArrowLeft") {
        setModalIndex((i) => (i - 1 + images.length) % images.length);
      } else if (e.key === "Escape") {
        setModalOpen(false);
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [modalOpen, images.length]);

  const openModal = (i) => {
    setModalIndex(i);
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);
  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="p-6 max-w-screen-xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Detection Gallery</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {total > 0
              ? `${total} detection image${total !== 1 ? "s" : ""}`
              : "Detection results"}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <FaCalendar className="text-slate-400 text-sm" />
          <select
            value={selectedDate}
            onChange={(e) => {
              setSelectedDate(e.target.value);
              setPage(1);
            }}
            className="text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white
              focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition"
          >
            <option value="">All Dates</option>
            {dateOptions.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Grid */}
      <div className="card">
        {loading ? (
          <div className="flex items-center justify-center py-24 gap-3 text-slate-400">
            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">Loading images…</span>
          </div>
        ) : images.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center">
              <FaSearch className="text-2xl text-slate-300" />
            </div>
            <div className="text-sm font-medium text-slate-500">
              No detection images found
            </div>
          </div>
        ) : (
          <div className="p-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((img, i) => (
              <div
                key={`${img.date}-${img.image}-${i}`}
                className="group rounded-2xl overflow-hidden border border-slate-100 bg-slate-50
                  cursor-pointer transition-all duration-200 hover:shadow-card-md hover:-translate-y-0.5"
                onClick={() => openModal(i)}
              >
                <DetectionOverlayImage
                  src={`${API_BASE_URL}/images/${img.date}/${img.image}`}
                  alt={img.image}
                  boxes={img.boxes || img.boxes_data?.boxes || []}
                  className="relative overflow-hidden"
                  imgClassName="w-full h-40 object-cover transition-transform duration-300 group-hover:scale-105"
                />

                <div className="px-3 py-2.5 space-y-1">
                  <div className="text-xs font-semibold text-slate-500">
                    {img.date}
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="flex items-center gap-1 text-blue-600">
                      <FaUsers className="text-[10px]" />
                      {img.person_count}
                    </span>
                    <span className="flex items-center gap-1 text-emerald-600">
                      <FaTruck className="text-[10px]" />
                      {img.machinery_count}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 px-5 pb-5">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="btn-secondary disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FaChevronLeft className="text-xs" /> Prev
            </button>

            <span className="text-sm text-slate-500">
              Page <span className="font-semibold text-slate-800">{page}</span> of{" "}
              {totalPages}
            </span>

            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="btn-secondary disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next <FaChevronRight className="text-xs" />
            </button>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {modalOpen && images.length > 0 && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center animate-fadeIn"
          onClick={closeModal}
        >
          <button
            className="absolute top-4 right-4 w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20
              text-white flex items-center justify-center transition"
            onClick={closeModal}
          >
            <FaTimes size={14} />
          </button>

          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-xl
              bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
            onClick={(e) => {
              e.stopPropagation();
              setModalIndex((modalIndex - 1 + images.length) % images.length);
            }}
          >
            <FaChevronLeft />
          </button>

          <div
            className="max-w-[85vw] flex flex-col items-center gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            <DetectionOverlayImage
              src={`${API_BASE_URL}/images/${images[modalIndex].date}/${images[modalIndex].image}`}
              alt={images[modalIndex].image}
              boxes={
                images[modalIndex].boxes ||
                images[modalIndex].boxes_data?.boxes ||
                []
              }
              className="max-h-[75vh]"
              imgClassName="max-h-[75vh] rounded-2xl shadow-2xl object-contain"
            />

            <div className="flex items-center gap-4 px-4 py-2.5 bg-white/10 rounded-xl text-sm text-white">
              <span className="font-medium">{images[modalIndex].date}</span>
              <span className="flex items-center gap-1 text-blue-300">
                <FaUsers className="text-xs" />
                {images[modalIndex].person_count} persons
              </span>
              <span className="flex items-center gap-1 text-emerald-300">
                <FaTruck className="text-xs" />
                {images[modalIndex].machinery_count} machinery
              </span>
            </div>
          </div>

          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-xl
              bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
            onClick={(e) => {
              e.stopPropagation();
              setModalIndex((modalIndex + 1) % images.length);
            }}
          >
            <FaChevronRight />
          </button>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs text-white/60">
            {modalIndex + 1} / {images.length}
          </div>
        </div>
      )}
    </div>
  );
}

export default DetectionGallery;